import React from "react";
import playerContext from "../data/playerContext";
import VideoToGpsMatchDialog from "../playlist/VideoToGpsMatchDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderOpen } from "@fortawesome/free-regular-svg-icons";
import OpenOrExtractFileDialog from "../fileSelection/OpenOrExtractFileDialog";
import { getFileName } from "../data/fileType";
import { loadFileAsText } from "../data/misc";
import { parseGpsData } from "../data/gpsData";

import "./videoPlayer.css";

export default React.forwardRef((props, ref) => (
  <VideoPlayer {...props} forwardedRef={ref} />
));

class VideoPlayer extends React.Component {
  constructor(props) {
    super(props);

    const url = new URLSearchParams(window.location.search);
    const loadNomad = url.has("nomadId") ? url.get("nomadId") : null;
    this.state = {
      gpsRows: [],
      videoUrl: null,
      playingName: null,
      showVideoToGpsMatchDialog: false,
      showOpenFileDialog: loadNomad != null,
      loadNomad,
    };
    this.videoPlayer = props.forwardedRef ?? React.createRef();
    this.closeVideoToGpsMatchDialog =
      this.closeVideoToGpsMatchDialog.bind(this);
    this.videoTimeUpdate = this.videoTimeUpdate.bind(this);
    this.metadataLoaded = this.metadataLoaded.bind(this);
    this.acceptVideoToGpsMatchDialog =
      this.acceptVideoToGpsMatchDialog.bind(this);

    this.handleSelectFiles = this.handleSelectFiles.bind(this);
    this.hideOpenFileDialog = this.hideOpenFileDialog.bind(this);
    this.filesSelected = this.filesSelected.bind(this);
    this.playEnded = this.playEnded.bind(this);

    this.playerContext = new playerContext(
      this.videoPlay.bind(this),
      this.videoPause.bind(this),
      this.setTime.bind(this),
      this.setPlaybackRate.bind(this),
      this.handleSelectFiles,
      this.refreshTime.bind(this)
    );
    this.playerContext.onContextStateChanged(
      this.playerStateChanged.bind(this)
    );
    props.setPlayerContext(this.playerContext);
  }

  async componentDidMount() {
    if (this.state.loadNomad) console.log(this.state.loadNomad);
  }

  playerStateChanged(state) {
    if (
      this.videoSequence !== state.videoToGpsFileMapping ||
      this.playlistIndex !== state.playlistIndex
    ) {
      this.handlePlaylistUpdates(
        state.videoToGpsFileMapping,
        state.playlistIndex
      );
    }
  }

  handlePlaylistUpdates(videoSequence, playlistIndex) {
    const videoFiles = Array.from(this.videoFiles);
    const gpsFiles = Array.from(this.gpsFiles);
    const videoIndex = videoFiles
      .map((item) => getFileName(item))
      .indexOf(videoSequence[playlistIndex].video);
    const dataIndex = gpsFiles
      .map((item) => getFileName(item))
      .indexOf(videoSequence[playlistIndex].gps);

    if (videoIndex !== this.currentPlayingIndex) {
      this.gpsFileInputChanged(this.gpsFiles[dataIndex]);
      this.videoFileInputChanged(this.videoFiles[videoIndex]);
    }
    this.videoSequence = videoSequence;
    this.playlistIndex = playlistIndex;
    this.currentPlayingIndex = videoIndex;
  }

  metadataLoaded(e) {
    this.videoPlayer.current.playbackRate = this.playerContext.playbackRate;

    this.setPlayerContextForTime(0, e.target.duration);
  }

  handleSelectFiles() {
    this.setState({ showOpenFileDialog: true });
  }

  refreshTime() {
    if (!this.videoPlayer.current) return;
    this.setPlayerContextForTime(this.videoPlayer.current.currentTime);
  }

  videoTimeUpdate(e) {
    const time = e.target.currentTime;

    this.setPlayerContextForTime(time, e.target.duration);
  }

  setPlayerContextForTime(time, duration) {
    const gpsData = this.props.gpsData.getDataAtTime(time);

    if (this.playerContext.gpsInformation.current !== gpsData) {
      this.playerContext.setGpsInformation({
        current: gpsData,
      });
    }
    this.playerContext.setVideoInformation({
      videoPosition: time,
      videoLength: duration,
    });
  }

  videoPlay() {
    if (!this.state.videoUrl) return;
    if (this.videoPlayer.current.paused) this.videoPlayer.current.play();
  }

  videoPause() {
    if (!this.state.videoUrl) return;
    if (!this.videoPlayer.current.paused) this.videoPlayer.current.pause();
  }

  playEnded() {
    if (this.playlistIndex === this.videoFiles.length - 1) {
      this.playerContext.playing = false;
      this.playerContext.notifyStateSubscribers();
      return;
    }

    this.playerContext.setPlaylistIndex(this.playlistIndex + 1);
  }

  closeVideoToGpsMatchDialog() {
    this.setState({ showVideoToGpsMatchDialog: false });
  }

  async acceptVideoToGpsMatchDialog(selections) {
    this.closeVideoToGpsMatchDialog();
    const sortedSelections = await this.setDefaultOrder(selections);
    this.playerContext.setPlayerFiles(sortedSelections, 0, true);
  }

  hideOpenFileDialog() {
    this.setState({ showOpenFileDialog: false, loadNomad: null });
  }

  async filesSelected(videoFiles, gpsFiles, selections = null) {
    this.setState({ showOpenFileDialog: false, loadNomad: null });

    this.videoFiles = videoFiles;
    this.gpsFiles = gpsFiles;
    this.currentPlayingIndex = -1;
    if (videoFiles.length === 1) {
      //don't show file matching dialog, just set the single item
      await this.acceptVideoToGpsMatchDialog([
        { video: getFileName(videoFiles[0]), gps: getFileName(gpsFiles[0]) },
      ]);
    } else if (selections) {
      await this.acceptVideoToGpsMatchDialog(selections);
    } else {
      this.setState({ showVideoToGpsMatchDialog: true });
    }
  }

  async setDefaultOrder(selections) {
    const selectionsWithTimes = await Promise.all(
      Array.from(this.gpsFiles).map(async (file, i) => {
        const fileName = getFileName(file);
        const selection = selections.find((x) => x.gps === fileName);
        const fileContents = await loadFileAsText(file);
        const fileType = fileName
          .substring(fileName.lastIndexOf(".") + 1)
          .toUpperCase();
        try {
          const parseFile = [...parseGpsData(fileContents, fileType)];
          let findValidRow = parseFile.find((item) => item.sortingKey);
          return { ...selection, sortingKey: findValidRow.sortingKey };
        } catch {
          console.error(
            `${fileName}: unable to parse GPS metadata, skipping file`
          );
          return null;
        }
      })
    );

    return selectionsWithTimes
      .filter((x) => x)
      .sort((a, b) => a.sortingKey - b.sortingKey);
  }

  //handles * video file uploads
  videoFileInputChanged(fileUnion) {
    fileUnion.case({
      FileSystem: (file) => {
        const fileUrl = URL.createObjectURL(file);

        const playingName = file.name.replace(/\.[^/.]+$/, ""); //remove file extension
        this.setState({
          videoUrl: fileUrl,
          playingName,
        });
        this.playerContext.playing = false;
        this.playerContext.playingName = playingName;
      },
      Url: (url, name) => {
        const playingName = name.replace(/\.[^/.]+$/, ""); //remove file extension
        this.setState({
          videoUrl: url,
          playingName,
        });
        this.playerContext.playing = false;
        this.playerContext.playingName = playingName;
      },
    });
  }

  gpsFileInputChanged(fileUnion) {
    this.props.gpsData &&
      this.props.gpsData.readGpsFile(fileUnion, (gpsRows, bufferedGeometry) =>
        this.playerContext.setGpsInformation(
          { allData: gpsRows, bufferedGeometry },
          true
        )
      );
  }

  setTime(seconds) {
    if (!this.state.videoUrl) return;

    if (seconds <= this.videoPlayer.current.duration)
      this.videoPlayer.current.currentTime = seconds;
  }

  setPlaybackRate(rate) {
    if (!this.videoPlayer.current) return;
    this.videoPlayer.current.playbackRate = rate;
  }

  render() {
    return (
      <>
        <div className="videoPlayerContainer">
          <div className="videoPlayer">
            {/*
        Note: 
            playsInline - allows mobile clients to play the video without going fullscreen
            pointerEvents: none - disables the context menu which would allow the user to bring back the controls
            controls - absent to remove the default video transport controls
         */}
            {this.state.videoUrl ? (
              <video
                ref={this.videoPlayer}
                className="video"
                playsInline
                muted
                src={this.state.videoUrl}
                onEnded={this.playEnded}
                onTimeUpdate={this.videoTimeUpdate}
                onLoadedMetadata={this.metadataLoaded}
                crossOrigin="anonymous"
                style={{ pointerEvents: "none" }}
              ></video>
            ) : (
              <div className="videoPlaceholder">
                <FontAwesomeIcon
                  title="Select Files"
                  icon={faFolderOpen}
                  size="10x"
                  onClick={this.handleSelectFiles}
                />
              </div>
            )}
            {this.state.playingName && (
              <div className="fileName">{this.state.playingName}</div>
            )}
          </div>
        </div>
        <VideoToGpsMatchDialog
          hidden={!this.state.showVideoToGpsMatchDialog}
          gpsFiles={this.gpsFiles}
          videoFiles={this.videoFiles}
          onAccept={this.acceptVideoToGpsMatchDialog}
          onReject={this.closeVideoToGpsMatchDialog}
        />
        <OpenOrExtractFileDialog
          show={this.state.showOpenFileDialog}
          onReject={this.hideOpenFileDialog}
          filesSelected={this.filesSelected}
          loadNomad={this.state.loadNomad}
          nomadApiUrl={this.props.nomadApiUrl}
        />
      </>
    );
  }
}
